/**
 * PWAUpdateBanner — offline PWA "new version available" prompt.
 *
 * Wired to `index.js` service-worker registration:
 *   - When a new SW is installed AND a controller already exists (i.e.
 *     an old version is currently running), `index.js` dispatches
 *     `sonspeak:sw-update` with { registration } in event.detail.
 *   - This component listens for that event, shows a small toast at
 *     the bottom-right, and on "Refresh" tap posts SKIP_WAITING to the
 *     waiting worker. That triggers `controllerchange`, which reloads.
 *
 * We deliberately DO NOT auto-refresh anymore (that could interrupt a
 * caregiver mid-type). The user must confirm.
 *
 * Escape hatch: if the toast is dismissed, the new SW still takes over
 * on the next natural reload / navigation.
 */
import { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";

export const SW_UPDATE_EVENT = "sonspeak:sw-update";

export default function PWAUpdateBanner() {
  const [registration, setRegistration] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onEvt = (e) => {
      // First update wins — don't stack toasts if several updatefounds
      // fire in quick succession.
      setRegistration((cur) => cur || e.detail?.registration || null);
      setDismissed(false);
    };
    window.addEventListener(SW_UPDATE_EVENT, onEvt);
    return () => window.removeEventListener(SW_UPDATE_EVENT, onEvt);
  }, []);

  if (!registration || dismissed) return null;

  const refresh = () => {
    const waiting = registration.waiting;
    if (waiting) {
      // Ask the waiting worker to activate; controllerchange in
      // index.js will call location.reload().
      waiting.postMessage("SKIP_WAITING");
    } else {
      // Fallback: just reload directly. The new SW will pick up.
      window.location.reload();
    }
  };

  return (
    <div
      className="fixed bottom-4 right-4 z-[70] max-w-xs brutal-card bg-[color:var(--gold)] p-3 shadow-lg"
      role="status"
      aria-live="polite"
      data-testid="pwa-update-banner"
    >
      <div className="flex items-start gap-2">
        <RefreshCw size={18} className="shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="display font-extrabold text-sm">Update available</div>
          <div className="text-xs text-[color:var(--ink)]/80 mb-2">
            A new SonSpeak version is ready. Tap refresh to get it.
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={refresh}
              className="pill-btn primary text-xs"
              data-testid="pwa-update-refresh"
            >
              Refresh now
            </button>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="pill-btn text-xs"
              aria-label="Dismiss"
              data-testid="pwa-update-dismiss"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
